// src/components/Income.js

import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import Navbar from './Navbar';
import '../styles/Income.css';
import { SERVER_URL } from './../config';
import { AuthContext } from './../AuthContext';

const Income = () => {
  const { token, isAuthenticated } = useContext(AuthContext);

  const [incomeEntries, setIncomeEntries] = useState([]);
  const [filteredIncomeEntries, setFilteredIncomeEntries] = useState([]);
  const [selectedIncome, setSelectedIncome] = useState(null);
  const [editableIncome, setEditableIncome] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');
  const [newIncome, setNewIncome] = useState({
    source: '',
    amount: '',
    date: '',
    active: true,
  });

  // States for filtering
  const [showActive, setShowActive] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  const fetchIncome = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${SERVER_URL}/income/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (Array.isArray(response.data)) {
        setIncomeEntries(response.data);
      } else {
        setIncomeEntries([]);
      }
    } catch (error) {
      console.error('Error fetching income entries:', error);
      setUpdateMessage('Failed to load income entries.');
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchIncome();
    }
  }, [isAuthenticated, fetchIncome]);

  const applyFilters = useCallback(() => {
    const filtered = incomeEntries.filter((income) => {
      const activeMatch = (showActive && income.active) || (showInactive && !income.active);
      return activeMatch;
    });
    setFilteredIncomeEntries(filtered);
  }, [incomeEntries, showActive, showInactive]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleFilterChange = (e) => {
    const { name, checked } = e.target;
    if (name === 'showActive') setShowActive(checked);
    if (name === 'showInactive') setShowInactive(checked);
  };

  const openModal = (income) => {
    setSelectedIncome(income);
    setEditableIncome({ ...income });
    setUpdateMessage('');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedIncome(null);
    setEditableIncome(null);
    setUpdateMessage('');
    setIsModalOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? e.target.checked : value;
    setEditableIncome((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleUpdate = async () => {
    if (!selectedIncome) return;
    // Exclude id and user_id from the request body
    const { id, user_id, ...updateData } = editableIncome;
    try {
      const response = await axios.patch(
        `${SERVER_URL}/income/${selectedIncome.id}`,
        updateData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      setIncomeEntries((prevIncome) =>
        prevIncome.map((inc) =>
          inc.id === selectedIncome.id ? response.data : inc
        )
      );
      setUpdateMessage('Income updated successfully.');
    } catch (error) {
      console.error('Error updating income:', error);
      setUpdateMessage('Failed to update income. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!selectedIncome) return;
    try {
      await axios.delete(`${SERVER_URL}/income/${selectedIncome.id}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      setIncomeEntries((prevIncome) =>
        prevIncome.filter((inc) => inc.id !== selectedIncome.id)
      );
      closeModal();
    } catch (error) {
      console.error('Error deleting income:', error);
    }
  };

  const handleCreateIncome = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${SERVER_URL}/income/`, newIncome, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      setIncomeEntries([...incomeEntries, response.data]);
      setShowCreateModal(false);
      setNewIncome({
        source: '',
        amount: '',
        date: '',
        active: true,
      });
    } catch (error) {
      console.error('Error creating income:', error);
    }
  };

  const handleNewInputChange = (e) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? e.target.checked : value;
    setNewIncome((prev) => ({ ...prev, [name]: newValue }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const formatter = new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
    return formatter.format(date);
  };

  return (
    <div className="income">
      <Navbar />
      <div className="content">
        <h1>Income</h1>
        <button className="create-income-button" onClick={() => setShowCreateModal(true)}>
          Add Income
        </button>
        <div className="filter-options">
          <label>
            <input
              type="checkbox"
              name="showActive"
              checked={showActive}
              onChange={handleFilterChange}
            />
            Show Active
          </label>
          <label>
            <input
              type="checkbox"
              name="showInactive"
              checked={showInactive}
              onChange={handleFilterChange}
            />
            Show Inactive
          </label>
        </div>
        <div className="income-container">
          <table className="income-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Active</th>
              </tr>
            </thead>
            <tbody>
              {filteredIncomeEntries.map((income, index) => (
                <tr
                  key={income.id}
                  className={index % 2 === 0 ? 'even' : 'odd'}
                  onClick={() => openModal(income)}
                >
                  <td>{income.source}</td>
                  <td>{income.amount}</td>
                  <td>{income.date ? formatDate(income.date) : 'N/A'}</td>
                  <td>{income.active ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="income-tiles">
            {filteredIncomeEntries.map((income) => (
              <div
                key={income.id}
                className="income-tile"
                onClick={() => openModal(income)}
              >
                <div className="income-source">{income.source}</div>
                <div className="income-amount">
                  <label>Amount: </label>
                  {income.amount}
                </div>
                <div className="income-date">
                  <label>Date: </label>
                  {income.date ? formatDate(income.date) : 'N/A'}
                </div>
                <div className="income-active">
                  <label>Active: </label>
                  {income.active ? 'Yes' : 'No'}
                </div>
              </div>
            ))}
          </div>
        </div>
        {isModalOpen && editableIncome && (
          <div className="modal">
            <div className="modal-content">
              <button
                type="button"
                className="modal-x"
                onClick={closeModal}
                aria-label="Close"
                title="Close"
              >
              </button>
              <h2>Edit Income</h2>
              <form>
                <label>
                  Source:
                  <input
                    type="text"
                    name="source"
                    value={editableIncome.source}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Amount:
                  <input
                    type="number"
                    name="amount"
                    value={editableIncome.amount}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Date:
                  <input
                    type="date"
                    name="date"
                    value={editableIncome.date || ''}
                    onChange={handleInputChange}
                  />
                </label>
                <label className="inline-checkbox">
                  Active:
                  <input
                    type="checkbox"
                    name="active"
                    checked={editableIncome.active}
                    onChange={handleInputChange}
                  />
                </label>
              </form>
              {updateMessage && (
                <div
                  className={`modal-message ${
                    updateMessage.includes('success') ? 'success' : 'error'
                  }`}
                >
                  {updateMessage}
                </div>
              )}
              <div className="modal-buttons">
                <button className="modal-button" onClick={handleUpdate}>
                  Update
                </button>
                <button className="modal-button delete-button" onClick={handleDelete}>
                  Delete
                </button>
                <button className="modal-button close-button" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {showCreateModal && (
          <div className="modal">
            <div className="modal-content">
              <button
                type="button"
                className="modal-x"
                onClick={() => setShowCreateModal(false)}
                aria-label="Close"
                title="Close"
              >
              </button>
              <h2>Create Income</h2>
              <form onSubmit={handleCreateIncome}>
                <label>
                  Source:
                  <input
                    type="text"
                    name="source"
                    value={newIncome.source}
                    onChange={handleNewInputChange}
                    required
                  />
                </label>
                <label>
                  Amount:
                  <input
                    type="number"
                    name="amount"
                    value={newIncome.amount}
                    onChange={handleNewInputChange}
                    required
                  />
                </label>
                <label>
                  Date:
                  <input
                    type="date"
                    name="date"
                    value={newIncome.date}
                    onChange={handleNewInputChange}
                    required
                  />
                </label>
                <label className="inline-checkbox">
                  Active:
                  <input
                    type="checkbox"
                    name="active"
                    checked={newIncome.active}
                    onChange={handleNewInputChange}
                  />
                </label>
                <button type="submit">Create</button>
              </form>
              <button
                className="modal-button close-button"
                onClick={() => setShowCreateModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Income;
